import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import PageLoader from './components/PageLoader';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './context/AuthContext';

// Lazy load all pages for code splitting
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const RwandaPatterns = lazy(() => import('./pages/RwandaPatterns'));
const BypassDetection = lazy(() => import('./pages/BypassDetection'));
const UnifiedSearch = lazy(() => import('./pages/UnifiedSearch'));
const RealTimeMonitor = lazy(() => import('./pages/RealTimeMonitor'));
const NetworkAnalysis = lazy(() => import('./pages/NetworkAnalysis'));
const Recordings = lazy(() => import('./pages/Recordings'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const IntelligenceDashboard = lazy(() => import('./pages/IntelligenceDashboard'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const AuditDashboard = lazy(() => import('./pages/AuditDashboard'));
const CaseManagement = lazy(() => import('./pages/CaseManagement'));
const AIChat = lazy(() => import('./pages/AIChat'));
const Workflows = lazy(() => import('./pages/Workflows'));
const InvestigationHub = lazy(() => import('./pages/InvestigationHub'));
const ThreatPyramid = lazy(() => import('./pages/ThreatPyramid'));
const SMSMessaging = lazy(() => import('./pages/SMSMessaging'));
const WebPhone = lazy(() => import('./pages/WebPhone'));
const LiveLocate = lazy(() => import('./pages/LiveLocate'));

// Admin Route Wrapper
const AdminRoute = ({ children }) => {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F5F7FA' }}>
                <Layout>
                  <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/cdr-search" element={<UnifiedSearch />} />
                      <Route path="/unified-search" element={<Navigate to="/cdr-search" replace />} />
                      <Route path="/vodacom" element={<Navigate to="/cdr-search" replace />} />
                      <Route path="/rwanda-patterns" element={<RwandaPatterns />} />
                      <Route path="/bypass-detection" element={<BypassDetection />} />
                      <Route path="/realtime" element={<RealTimeMonitor />} />
                      <Route path="/network" element={<NetworkAnalysis />} />
                      <Route path="/recordings" element={<Recordings />} />
                      <Route path="/analytics" element={<IntelligenceDashboard />} />
                      <Route path="/intelligence" element={<Navigate to="/analytics" replace />} />
                      <Route path="/executive-briefing" element={<Navigate to="/analytics" replace />} />
                      <Route path="/advanced-analytics" element={<Navigate to="/analytics" replace />} />
                      <Route path="/flagged-numbers" element={<Navigate to="/reports" replace />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route
                        path="/users"
                        element={
                          <AdminRoute>
                            <UserManagement />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/audit"
                        element={
                          <AdminRoute>
                            <AuditDashboard />
                          </AdminRoute>
                        }
                      />
                      <Route path="/cases" element={<CaseManagement />} />
                      <Route path="/investigation" element={<InvestigationHub />} />
                      <Route path="/threat-pyramid" element={<ThreatPyramid />} />
                      <Route path="/sms" element={<SMSMessaging />} />
                      <Route path="/phone" element={<WebPhone />} />
                      <Route path="/live-locate" element={<LiveLocate />} />
                      <Route path="/ai-chat" element={<AIChat />} />
                      <Route
                        path="/workflows"
                        element={
                          <AdminRoute>
                            <Workflows />
                          </AdminRoute>
                        }
                      />
                    </Routes>
                  </Suspense>
                  </ErrorBoundary>
                </Layout>
              </Box>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
